import React from 'react';

// Ce que le client a rempli lui-même, tel qu'il l'a envoyé.
//
// Tout était stocké sur le lead (context) et affiché nulle part côté Pro : on
// voyait un total sans savoir de quels postes il venait, et le message d'une
// demande de contact — « une maison qui comprend 3 appartements » — n'était
// lisible que dans l'email d'origine. Pire, l'outil de devis tire de
// l'estimatif une conversion approximative (dimensions déduites de la surface,
// postes non reconnus écartés) qui devenait la seule trace. Ce bloc garde
// l'original consultable, quelle que soit la porte d'entrée du client.

const TITRES = {
  devis: 'Estimatif rempli en ligne',
  contact: 'Demande envoyée par le formulaire',
  assistant: 'Échange avec l’assistant',
};

export function aUneDemande(lead) {
  const c = lead?.context;
  if (!c) return false;
  return !!(c.rooms?.length || c.message || c.typeProjet || c.items?.length || c.estimate);
}

export function titreDemande(lead) {
  return TITRES[lead?.source] || 'Ce que le client a envoyé';
}

// Résumé d'une ligne, pour l'étiquette du bouton qui ouvre le panneau.
export function resumeDemande(lead) {
  const c = lead?.context;
  if (!c) return '';
  if (c.rooms?.length) return `${c.rooms.length} ${c.rooms.length > 1 ? 'pièces' : 'pièce'}`;
  if (c.items?.length) return `${c.items.length} ${c.items.length > 1 ? 'postes' : 'poste'}`;
  if (c.message) return 'message';
  if (c.typeProjet) return c.typeProjet.toLowerCase();
  return 'détails';
}

function Champ({ libelle, children }) {
  if (!children) return null;
  return (
    <div className="flex flex-wrap gap-x-2 text-sm">
      <span className="text-slate-400 shrink-0">{libelle}</span>
      <span className="text-slate-800 font-medium">{children}</span>
    </div>
  );
}

export default function EstimatifClient({ lead, compact = false }) {
  const ctx = lead?.context;
  if (!aUneDemande(lead)) return null;

  const pieces = ctx.rooms || [];

  return (
    <div className="text-sm">
      {/* ── Demande de contact : le texte libre est l'essentiel ── */}
      {(ctx.typeProjet || ctx.surface || ctx.codePostal) && (
        <div className="space-y-1 mb-3">
          <Champ libelle="Projet">{ctx.typeProjet}</Champ>
          <Champ libelle="Surface">{ctx.surface}</Champ>
          <Champ libelle="Code postal">{ctx.codePostal}</Champ>
        </div>
      )}
      {ctx.message && (
        <blockquote className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 mb-3 whitespace-pre-line text-slate-700">
          {ctx.message}
        </blockquote>
      )}

      {/* ── Estimatif en ligne : les hypothèses, puis le détail par pièce ── */}
      {pieces.length > 0 && (
        <>
          {/* Le même total ne veut pas dire la même chose selon l'état du bien.
              Absent des demandes d'avant le 24/09/2026. */}
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 mb-3">
            {ctx.etatLabel && <span>État déclaré : <strong className="text-slate-700">{ctx.etatLabel}</strong></span>}
            {ctx.tvaLabel && <span>TVA : <strong className="text-slate-700">{ctx.tvaLabel}</strong></span>}
            {ctx.etages?.length > 0 && (
              <span>Niveaux : <strong className="text-slate-700">{ctx.etages.join(', ')}</strong></span>
            )}
            {!ctx.etatLabel && (
              <span className="text-slate-400">État du bien et TVA non transmis (demande antérieure au 24/09/2026)</span>
            )}
          </div>

          <div className="space-y-3">
            {pieces.map((piece, i) => (
              <div key={i} className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="flex items-baseline justify-between gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="font-semibold text-slate-800">{piece.name}</span>
                  <span className="text-xs text-slate-500 shrink-0">
                    {piece.surface ? `${piece.surface} m²` : '—'}
                  </span>
                </div>
                <table className="w-full">
                  <tbody>
                    {(piece.lines || []).map((ligne, j) => (
                      <tr key={j} className="border-b border-slate-50 last:border-0">
                        <td className="px-3 py-1.5 text-slate-700">{ligne.label}</td>
                        <td className="px-3 py-1.5 text-xs text-slate-500 whitespace-nowrap">{ligne.detail}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap text-slate-600">{ligne.price}</td>
                      </tr>
                    ))}
                    {(piece.lines || []).length === 0 && (
                      <tr><td className="px-3 py-2 text-xs text-slate-400">Aucun poste chiffré</td></tr>
                    )}
                  </tbody>
                </table>
                {piece.subtotal && (
                  <div className="flex items-baseline justify-between px-3 py-1.5 text-xs bg-slate-50 border-t border-slate-100">
                    <span className="text-slate-400">Sous-total</span>
                    <span className="font-semibold text-slate-600 tabular-nums">{piece.subtotal}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Assistant : les postes retenus au fil de la conversation ── */}
      {ctx.items?.length > 0 && (
        <ul className="rounded-lg border border-slate-200 divide-y divide-slate-100 mb-3">
          {ctx.items.map((item, i) => (
            <li key={i} className="px-3 py-1.5 text-slate-700">
              {typeof item === 'string' ? item : (item.label || JSON.stringify(item))}
            </li>
          ))}
        </ul>
      )}

      {(ctx.total || ctx.estimate) && (
        <div className="flex items-baseline justify-between gap-3 mt-3 pt-3 border-t border-slate-200">
          <span className="text-xs text-slate-500">
            Estimation indicative annoncée au client{ctx.totalHT ? ` (${ctx.totalHT} HT)` : ''}
          </span>
          <span className="font-bold text-orange-600 tabular-nums whitespace-nowrap">
            {ctx.total ? `${ctx.total} TTC` : ctx.estimate}
          </span>
        </div>
      )}

      {!compact && pieces.length > 0 && (
        <p className="text-xs text-slate-400 mt-3">
          Chiffrage automatique du configurateur, sur des surfaces déclarées par le client.
          L'outil de devis en reprend les postes qu'il sait reconnaître ; c'est cette liste-ci
          qui fait foi sur ce qui a été demandé.
        </p>
      )}
    </div>
  );
}
